import {Nut_Settings_User} from 'nutshell-core/settings-constants';

import {Checkbox} from 'shells/checkbox/checkbox';
import {colors} from 'shells/colors/colors';
import {Dialog} from 'shells/dialog/dialog';
// @ts-expect-error
import {UpgradeArrowIcon} from 'shells/icon/svg/upgrade-arrow-icon';
import {LoadingText} from 'shells/loading-placeholder/loading-text';

import {AiFeatureType, LimitedType} from 'nutshell-graphql-types';
import type {GetSessionUser_loggedInUser as User} from 'nutshell-graphql-types';

import type {PhoneCallParameters} from '../../phone/call';

import {useUpdateUserSetting} from '../core/use-update-user-setting';
import {useGetLimitsByType} from '../limits/use-get-limits-by-type';
import {useIsAiFeatureEnabled} from '../setup/ai-features/graphql/hooks/use-is-ai-feature-enabled';

import type {PopoverData} from './click-to-call-legacy-event-listener';

interface Props {
    popoverData: PopoverData;
    user: User;
    handleCloseDialog: () => void;
    handleCall: (data: PhoneCallParameters) => void;
    onOpenFeatureGate: () => void;
}

export function InitiateCallWithOptionsPopover(props: Props) {
    const updateSetting = useUpdateUserSetting();
    const hasAiOutcomes = NutClientConfig.hasAiOutcomesFF;
    const limitsType = hasAiOutcomes ? LimitedType.AI_OUTCOMES : LimitedType.AI_SUMMARIES;
    const {limits, isLoading: isLoadingLimits} = useGetLimitsByType(limitsType);
    const {isEnabled: clickToCallIsEnabled, isLoading: isLoadingAiFeatureUsage} =
        useIsAiFeatureEnabled(AiFeatureType.CLICK_TO_CALL_NOTETAKER);

    const hasAvailableSummaries = limits ? limits.canAdd : false;

    const handleCheckboxChange = (settingName: string, isChecked: boolean) => {
        const value = isChecked ? '1' : '0';
        let propertyOnUser = '';
        if (settingName === Nut_Settings_User.SETTING_C2C_AUDIO_RECORDING_ENABLED) {
            propertyOnUser = 'clickToCallRecordingEnabled';
        } else if (settingName === Nut_Settings_User.SETTING_C2C_TRANSCRIPTION_ENABLED) {
            propertyOnUser = 'clickToCallTranscriptionEnabled';
        } else if (settingName === Nut_Settings_User.SETTING_C2C_SUMMARY_ENABLED) {
            propertyOnUser = 'clickToCallSummaryEnabled';
        }

        updateSetting({
            userId: props.user.id,
            settingName,
            value,
            propertyOnUser,
        });
    };

    const handlePrimaryClick = () => {
        const shouldTranscribeAndSummarize =
            hasAvailableSummaries && props.user.clickToCallSummaryEnabled;
        const callParameters: PhoneCallParameters = {
            entityId: props.popoverData.entityId,
            phoneNumber: {
                E164: props.popoverData.rawPhoneNumber,
                numberFormatted: props.popoverData.formattedPhoneNumber,
            },
            shouldRecord: props.user.clickToCallRecordingEnabled,
            shouldTranscribe: shouldTranscribeAndSummarize,
            shouldSummarize: shouldTranscribeAndSummarize,
        };

        props.handleCall(callParameters);
    };

    const _transcribeCheckboxSection = (
        <Checkbox
            label='Create audio transcript'
            onChange={(isChecked) => {
                handleCheckboxChange(
                    Nut_Settings_User.SETTING_C2C_TRANSCRIPTION_ENABLED,
                    isChecked
                );
            }}
            checked={props.user.clickToCallTranscriptionEnabled}
        />
    );

    let summaryCheckboxSection;
    if (!clickToCallIsEnabled || isLoadingAiFeatureUsage) {
        summaryCheckboxSection = null;
    } else if (isLoadingLimits) {
        summaryCheckboxSection = (
            <Checkbox label={<LoadingText width={112} height={12} />} disabled={true} />
        );
    } else if (hasAvailableSummaries) {
        summaryCheckboxSection = (
            <Checkbox
                label='Summarize this call'
                onChange={(isChecked) => {
                    handleCheckboxChange(Nut_Settings_User.SETTING_C2C_SUMMARY_ENABLED, isChecked);
                }}
                checked={props.user.clickToCallSummaryEnabled}
            />
        );
    } else {
        summaryCheckboxSection = (
            <div
                className='flex align-center gap-6 pointer-cursor'
                onClick={() => {
                    props.onOpenFeatureGate();
                    // Need to close the popover so that we
                    // don't conflict with the feature gate
                    // modal
                    props.handleCloseDialog();
                }}
            >
                <div className='disabled'>
                    <Checkbox label='Summarize this call' />
                </div>
                <UpgradeArrowIcon size={12} wrapWithDiv={false} color={colors.blue} />
            </div>
        );
    }

    return (
        <Dialog
            anchor={null}
            anchorElementId={props.popoverData.anchorElementId}
            question={`Call ${props.popoverData.formattedPhoneNumber}?`}
            primaryText='Call'
            tertiaryText='Cancel'
            onPrimary={handlePrimaryClick}
            onTertiary={() => props.handleCloseDialog()}
            primaryVariant='save'
            tertiaryVariant='secondary'
            onBlur={() => props.handleCloseDialog()}
            location='bottom-left'
        >
            <div className='mt-8 flex flex-dir-col gap-8'>
                <Checkbox
                    label='Record audio conversation'
                    onChange={(isChecked) => {
                        handleCheckboxChange(
                            Nut_Settings_User.SETTING_C2C_AUDIO_RECORDING_ENABLED,
                            isChecked
                        );
                    }}
                    checked={props.user.clickToCallRecordingEnabled}
                />
                {summaryCheckboxSection}
            </div>
        </Dialog>
    );
}
